/**
 * WebsitePublicPage - Public page displaying a website with AI-generated content
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getWebsiteByUuid } from '../../services/websitesService';
import Menu from '../public/Menu';
import Footer from '../public/Footer';
import { useLanguage } from '../../context/LanguageContext';
import ReportAbuseModal from '../ui/ReportAbuseModal';
import { Flag } from 'lucide-react';

const WebsitePublicPage = () => {
    const { uuid } = useParams();
    const { t, language } = useLanguage();
    const [website, setWebsite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showReportModal, setShowReportModal] = useState(false);

    useEffect(() => {
        const loadWebsite = async () => {
            if (!uuid) {
                setError('Invalid website ID');
                setLoading(false);
                return;
            }

            try {
                const data = await getWebsiteByUuid(uuid);
                if (data) {
                    setWebsite(data);
                } else {
                    setError('Website not found');
                }
            } catch (err) {
                console.error('Error loading website:', err);
                setError('Error loading website');
            } finally {
                setLoading(false);
            }
        };

        loadWebsite();
    }, [uuid]);

    // Parse social links if available
    const getSocialLinks = () => {
        if (!website?.socialLinks) return {};
        try {
            return typeof website.socialLinks === 'string'
                ? JSON.parse(website.socialLinks)
                : website.socialLinks;
        } catch {
            return {};
        }
    };

    // Parse keywords into array (get from current language)
    const getKeywords = () => {
        const langCode = language?.substring(0, 2) || 'fr';
        const keywords = website?.[`keywords_${langCode}`] || '';
        if (!keywords) return [];
        return keywords.split(',').map(k => k.trim()).filter(Boolean);
    };

    // Get display fields in current language
    const langCode = language?.substring(0, 2) || 'fr';
    const displayTitle = website?.[`title_${langCode}`] || website?.title || '';
    const displayDescription = website?.[`description_${langCode}`] || '';
    const displayCategory = website?.[`category_${langCode}`] || '';

    const socialLinks = getSocialLinks();
    const keywords = getKeywords();

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
            </div>
        );
    }

    if (error || !website) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <Menu onLogin={() => window.location.href = '/login'} />
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="text-center">
                        <div className="text-6xl mb-4">🔍</div>
                        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
                            {language === 'fr' ? 'Site web non trouvé' : 'Website not found'}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {language === 'fr'
                                ? 'Ce site web n\'existe pas ou n\'est plus disponible.'
                                : 'This website does not exist or is no longer available.'}
                        </p>
                        <Link
                            to="/"
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            ← {language === 'fr' ? 'Retour à l\'accueil' : 'Back to home'}
                        </Link>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <Menu onLogin={() => window.location.href = '/login'} />

            {/* Hero Section */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                <div className="max-w-6xl mx-auto px-4 py-16">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        {/* Website Snapshot */}
                        {website.snapshotPath && (
                            <div className="w-full md:w-1/3 flex-shrink-0">
                                <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
                                    <img
                                        src={website.snapshotPath}
                                        alt={displayTitle || website.url}
                                        className="w-full h-auto"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Title and Category */}
                        <div className="flex-1 text-center md:text-left">
                            {displayCategory && (
                                <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm mb-4">
                                    {displayCategory}
                                </span>
                            )}
                            <h1 className="text-4xl md:text-5xl font-bold mb-4">
                                {displayTitle || new URL(website.url).hostname}
                            </h1>
                            <a
                                href={website.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors text-lg"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                {website.url}
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="max-w-6xl mx-auto px-4 py-12">
                <div className="grid md:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="md:col-span-2 space-y-8">
                        {/* Description */}
                        {displayDescription && (
                            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                                <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
                                    {language === 'fr' ? 'À propos' : 'About'}
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                                    {displayDescription}
                                </p>
                            </div>
                        )}

                        {/* Keywords */}
                        {keywords.length > 0 && (
                            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                                    {language === 'fr' ? 'Mots-clés' : 'Keywords'}
                                </h2>
                                <div className="flex flex-wrap gap-2">
                                    {keywords.map((keyword, idx) => (
                                        <span
                                            key={idx}
                                            className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm"
                                        >
                                            {keyword}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Contact Information */}
                        {(website.contactEmail || website.contactPhone || website.address) && (
                            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                                    {language === 'fr' ? 'Contact' : 'Contact'}
                                </h2>
                                <div className="space-y-4">
                                    {website.contactEmail && (
                                        <a
                                            href={`mailto:${website.contactEmail}`}
                                            className="flex items-center gap-3 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                        >
                                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                            <span className="break-all">{website.contactEmail}</span>
                                        </a>
                                    )}

                                    {website.contactPhone && (
                                        <a
                                            href={`tel:${website.contactPhone}`}
                                            className="flex items-center gap-3 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                        >
                                            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                                                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                </svg>
                                            </div>
                                            <span>{website.contactPhone}</span>
                                        </a>
                                    )}

                                    {website.address && (
                                        <div className="flex items-start gap-3 text-gray-600 dark:text-gray-300">
                                            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                            </div>
                                            <span>{website.address}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Social Links */}
                        {Object.keys(socialLinks).some(key => socialLinks[key]) && (
                            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                                    {language === 'fr' ? 'Réseaux sociaux' : 'Social Media'}
                                </h2>
                                <div className="flex flex-wrap gap-3">
                                    {socialLinks.facebook && (
                                        <a
                                            href={socialLinks.facebook}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-12 h-12 bg-[#1877F2] rounded-lg flex items-center justify-center text-white hover:opacity-80 transition-opacity"
                                            title="Facebook"
                                        >
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                            </svg>
                                        </a>
                                    )}

                                    {socialLinks.linkedin && (
                                        <a
                                            href={socialLinks.linkedin}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-12 h-12 bg-[#0A66C2] rounded-lg flex items-center justify-center text-white hover:opacity-80 transition-opacity"
                                            title="LinkedIn"
                                        >
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                            </svg>
                                        </a>
                                    )}

                                    {socialLinks.instagram && (
                                        <a
                                            href={socialLinks.instagram}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-12 h-12 bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] rounded-lg flex items-center justify-center text-white hover:opacity-80 transition-opacity"
                                            title="Instagram"
                                        >
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                            </svg>
                                        </a>
                                    )}

                                    {socialLinks.twitter && (
                                        <a
                                            href={socialLinks.twitter}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-12 h-12 bg-black rounded-lg flex items-center justify-center text-white hover:opacity-80 transition-opacity"
                                            title="X (Twitter)"
                                        >
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                            </svg>
                                        </a>
                                    )}

                                    {socialLinks.youtube && (
                                        <a
                                            href={socialLinks.youtube}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-12 h-12 bg-[#FF0000] rounded-lg flex items-center justify-center text-white hover:opacity-80 transition-opacity"
                                            title="YouTube"
                                        >
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                            </svg>
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Visit Website CTA */}
                        <a
                            href={website.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white text-center py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-800 transition-all shadow-lg"
                        >
                            {language === 'fr' ? 'Visiter le site web' : 'Visit Website'}
                            <svg className="inline-block ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                        </a>

                        {/* Owner Info */}
                        {website.peopleUuid && (
                            <Link
                                to={`/people/${website.peopleUuid}`}
                                className="block bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 hover:shadow-xl transition-shadow"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                        <svg className="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                            {language === 'fr' ? 'Ajouté par' : 'Added by'}
                                        </div>
                                        <div className="font-medium text-gray-800 dark:text-white">
                                            {website.firstName} {website.lastName}
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        )}

                        {/* Report button */}
                        <button
                            onClick={() => setShowReportModal(true)}
                            className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors mt-4"
                        >
                            <Flag className="w-4 h-4" />
                            {language === 'fr' ? 'Signaler un abus' : 'Report abuse'}
                        </button>
                    </div>
                </div>
            </div>

            <Footer />

            {/* Report Modal */}
            <ReportAbuseModal
                isOpen={showReportModal}
                onClose={() => setShowReportModal(false)}
                contentType="website"
                contentId={website?.id}
                contentUuid={website?.uuid}
                contentTitle={displayTitle || website?.url}
            />
        </div>
    );
};

export default WebsitePublicPage;
